import { Button } from '@/components/ui/button';
import { CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { User } from '@/models/user';
import { OptimizedImage } from './OptimizedImage';

// Servida desde public/ con URL estable para poder precargarla (preload) e
// inyectarla en el HTML prerenderizado como elemento LCP descubrible sin JS.
const homeImg = '/home.webp';

interface HeroParams {
  user: User
}

export function Hero({ user } : HeroParams) {
  const navigate = useNavigate();

  return (
    <section className="pt-12 pb-12 xl:pt-8 xl:pb-8 bg-gradient-to-b from-violet-50 to-white">
      <div className="container px-4 md:px-6 mx-auto max-w-6xl">
        <div className="grid gap-8 lg:grid-cols-[58%_42%] lg:gap-12 items-center justify-center">
          <div className="space-y-8 text-center lg:text-left">
            <div className="space-y-5">
              <span className="text-sm font-semibold tracking-widest text-violet-600 uppercase">
                Conectamos personas con abogados
              </span>
              <h1 className="text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl leading-tight">
                La forma mas simple de{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-600 to-purple-600">encontrar Abogado</span>
              </h1>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
                Contanos tu problema y empezá a recibir propuestas de Abogados de nuestra red.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                onClick={() => {
                  if (user.role === "lawyer") {
                    navigate("/lawyer/marketplace");
                  } else {
                    navigate("/user/cases/create");
                  }
                }} >
                Buscar Abogado
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="text-lg px-8 py-6 bg-white border-2"
                onClick={() => {
                  navigate("/marketplace");
                }} >
                Ver Casos
              </Button>
            </div>

            <div className="flex items-center space-x-8 text-sm text-gray-600 justify-center lg:justify-start">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Búsqueda 100% gratuita</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>Abogados verificados</span>
              </div>
            </div>
          </div>

          <div className="relative w-full max-w-xl mx-auto xl:py-8 flex justify-center">
            <OptimizedImage
              src={homeImg}
              alt="Plataforma Abogalia conectando clientes con abogados verificados"
              width={1080}
              height={989}
              priority={true}
              className="w-full h-auto rounded-lg"
            />
          </div>
        </div>
      </div>
    </section>
  );
}